import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useEmojiWasm } from '@pkg/hooks';
import { useGetYatPageDetails } from '@pkg/hooks/queryHooks/useYatPageRequests.ts';
import { YatPageVersion } from '@pkg/types';

import config from '@app/config';
import { useEmojisStore } from '@app/store/zustand';
import { useUserStore } from '@app/store/zustand/user/useUserStore.ts';
import { useYatPageStore } from '@app/store/zustand/yat-page/useYatPageStore.ts';

export function useProfilePageInit() {
    const { emojiWasm } = useEmojiWasm();
    const { yatParam } = useParams();

    const isValidYat = useMemo(() => {
        if (yatParam) {
            return emojiWasm?.is_valid_emoji_id(yatParam, config.maxLengthYat);
        } else return false;
    }, [emojiWasm, yatParam]);

    const {
        data: yatPageRes,
        isLoading: isYatPageResLoading,
        refetch,
    } = useGetYatPageDetails({ yat: yatParam, disabled: !isValidYat });

    const userEmojis = useEmojisStore((s) => s.emojis);
    const currentUserId = useUserStore((s) => s.userId);

    const setVersion = useYatPageStore((s) => s.setVersion);
    const setDetails = useYatPageStore((s) => s.setDetails);
    const eid = useYatPageStore((s) => s.eid);

    const yatPageVersion = useMemo(() => {
        const loading = isYatPageResLoading || eid !== yatParam;
        if (loading) {
            return YatPageVersion.LOADING;
        }
        if (!!emojiWasm && !loading && !isValidYat) {
            return YatPageVersion.NOT_FOUND;
        }

        return yatPageRes?.version;
    }, [eid, emojiWasm, isValidYat, isYatPageResLoading, yatPageRes?.version, yatParam]);

    const handleSetDetails = useCallback(() => {
        const details = yatPageRes?.details;
        const userOwnsYat = yatParam ? !!userEmojis.find((eid) => eid === yatParam) : false;
        const isOwner = !currentUserId ? false : currentUserId === yatPageRes?.details?.user_id || userOwnsYat;
        if (details) {
            setDetails({ ...details, eid: details.eid || yatParam }, isOwner);
        } else {
            setDetails({ eid: yatParam }, isOwner);
        }
    }, [yatPageRes?.details, yatParam, userEmojis, currentUserId, setDetails]);

    useLayoutEffect(() => {
        setVersion(yatPageVersion);
    }, [setVersion, yatPageVersion]);

    useLayoutEffect(() => {
        if (yatParam && eid && yatParam !== eid) {
            refetch().then((r) => {
                if (r?.data?.version) {
                    setVersion(r.data.version);
                }
                handleSetDetails();
            });
        }
    }, [eid, refetch, setVersion, yatPageVersion, yatParam, handleSetDetails]);

    useEffect(() => {
        const version = isYatPageResLoading ? YatPageVersion.LOADING : yatPageRes?.version;
        setVersion(version);
        handleSetDetails();
    }, [setVersion, yatPageRes?.version, handleSetDetails, isYatPageResLoading]);
}
